import { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { REPORT_GEO_IMPORT_PATH } from "./lib/report-paths";
import { AppLayout } from "./components/app-layout";
import LoginPage from "./pages/LoginPage";
import StatusPage from "./pages/StatusPage";
import DashboardPage from "./pages/DashboardPage";
import ChangelogPage from "./pages/ChangelogPage";
import DocsPage from "./pages/DocsPage";
import UsersPage from "./pages/UsersPage";
import CountriesPage from "./pages/geo/CountriesPage";
import StatesPage from "./pages/geo/StatesPage";
import CitiesPage from "./pages/geo/CitiesPage";
import CityDetailPage from "./pages/geo/CityDetailPage";
import RoutesPage from "./pages/geo/RoutesPage";
import RouteDetailPage from "./pages/geo/RouteDetailPage";
import HelpPage from "./pages/HelpPage";
import AdminGuidePage from "./pages/docs/AdminGuidePage";
import GeoImportReportPage from "./pages/docs/GeoImportReportPage";
import RoutesImportReportPage from "./pages/docs/RoutesImportReportPage";
import OperatorGuidePage from "./pages/docs/OperatorGuidePage";
import CrudEntitiesGuidePage from "./pages/docs/CrudEntitiesGuidePage";
import AddFieldsGuidePage from "./pages/docs/AddFieldsGuidePage"
import ExportMappingsPage from "./pages/docs/ExportMappingsPage"
import DbDrizzleGuidePage from "./pages/docs/DbDrizzleGuidePage"
import WarningsCalculationPage from "./pages/docs/WarningsCalculationPage"
import CargoDocumentsRulesPage from "./pages/docs/CargoDocumentsRulesPage"
import SecurityPendingPage from "./pages/docs/SecurityPendingPage"
import BateaMobileFlowPage from "./pages/docs/BateaMobileFlowPage"
import ShipmentTripStatusesPage from "./pages/docs/ShipmentTripStatusesPage"
import ShipmentMobileFlowPage from "./pages/docs/ShipmentMobileFlowPage"
import ShipmentAlertsPage from "./pages/docs/ShipmentAlertsPage"
import SplitTripPage from "./pages/docs/SplitTripPage"
import TripActionsPage from "./pages/docs/TripActionsPage"
import TripsExportGuidePage from "./pages/docs/TripsExportGuidePage"
import CargoExportGuidePage from "./pages/docs/CargoExportGuidePage"
import BillingImportGuidePage from "./pages/docs/BillingImportGuidePage"
import ContractsGuidePage from "./pages/docs/ContractsGuidePage"
import BateaMultiLegPage from "./pages/docs/BateaMultiLegPage"
import ServiceTypesPage from "./pages/trips/ServiceTypesPage";
import TripsPage from "./pages/trips/TripsPage";
import ShipmentsPage from "./pages/shipments/ShipmentsPage";
import ShipmentsBillingPage from "./pages/shipments/ShipmentsBillingPage";
import ShipmentDetailPage from "./pages/shipments/ShipmentDetailPage";
import PublicTripPlanPage from "./pages/PublicTripPlanPage";
import PartiesPage from "./pages/parties/PartiesPage";
import CargoProductsPage from "./pages/cargos/CargoProductsPage";
import CargoProductDetailPage from "./pages/cargos/CargoProductDetailPage";
import CargoTypesPage from "./pages/cargos/CargoTypesPage";
import CargasNacionalPage from "./pages/cargos/CargasNacionalPage";
import CargasInternacionalPage from "./pages/cargos/CargasInternacionalPage";
import CargasBateaPage from "./pages/cargos/CargasBateaPage";
import CargasInformesPage from "./pages/cargos/CargasInformesPage";
import * as Sentry from "@sentry/react";
import { isAuthenticated, getAuth, clearAuth } from "./lib/auth";
import { rolesWithPermission } from "@cdc-sccr/shared";
import { SessionExpiredDialog } from "./components/session-expired-dialog";
import { useUnauthorizedWatcher } from "./hooks/use-unauthorized-watcher";
import UnitTypesPage from "./pages/unit-types/UnitTypesPage";
import UnitsPage from "./pages/units/UnitsPage";
import UnitDetailPage from "./pages/units/UnitDetailPage";
import DriversPage from "./pages/drivers/DriversPage"
import DriverDetailPage from "./pages/drivers/DriverDetailPage"
import DriverRemitosPage from "./pages/drivers/DriverRemitosPage"
import ClientsPage from "./pages/clients/ClientsPage"
import ClientDetailPage from "./pages/clients/ClientDetailPage"
import DocTypesPage from "./pages/doc-types/DocTypesPage";
import ShipmentDocTypesPage from "./pages/shipment-doc-types/ShipmentDocTypesPage";
import CurrenciesPage from "./pages/currencies/CurrenciesPage";
import PayConceptsPage from "./pages/pay-concepts/PayConceptsPage";
import SmartGTPage from "./pages/smart-gt/SmartGTPage";
import MobilesPage from "./pages/mobiles/MobilesPage";
import AuditPage from "./pages/system/AuditPage";
import RolesPage from "./pages/system/RolesPage";
import KpiPage from "./pages/system/KpiPage";
import AppSettingsPage from "./pages/system/AppSettingsPage";
import RolesPermissionsGuidePage from "./pages/docs/RolesPermissionsGuidePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import DriverCrtsPage from "./pages/smart-gt/DriverCrtsPage";
import SmartGtV2FormatPage from "./pages/docs/SmartGtV2FormatPage";
import CrtImportLogsPage from "./pages/smart-gt/CrtImportLogsPage";
import ContractsPage from "./pages/relations/ContractsPage";
import ContractDetailPage from "./pages/relations/ContractDetailPage";
import FinalDestinationsPage from "./pages/batea/FinalDestinationsPage";
import { Toaster } from "sonner";

// Role sets derived from the central permissions config — edit permissions.ts, not these.
const R_GEO       = rolesWithPermission("geo",       "visit")
const R_CONTRACTS = rolesWithPermission("contracts", "visit")
const R_TRIPS    = rolesWithPermission("trips",     "visit")
const R_SHIPMENT = rolesWithPermission("shipments", "visit")
const R_CARGO    = rolesWithPermission("cargo",     "visit")
const R_SMARTGT  = rolesWithPermission("smartgt",   "visit")
const R_UNITS    = rolesWithPermission("units",     "visit")
const R_DRIVERS  = rolesWithPermission("drivers",   "visit")
const R_CLIENTS  = rolesWithPermission("clients",   "visit")
const R_CONFIG   = rolesWithPermission("config",    "visit")
const R_SHIPMENT_DOC_TYPES = rolesWithPermission("shipmentDocTypes", "visit")
const R_MOBILES  = rolesWithPermission("mobiles",   "visit")
const R_PARTIES  = rolesWithPermission("parties",   "visit")
const R_USERS    = rolesWithPermission("users",     "visit")
const R_ROLES    = rolesWithPermission("roles",     "visit")
const R_AUDIT    = rolesWithPermission("audit",     "visit")
const R_REPORTS  = rolesWithPermission("reports",   "visit")
const R_DOCS     = rolesWithPermission("docs",      "visit")
const R_BATEA    = rolesWithPermission("batea",     "visit")

function RequireAuth({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const authenticated = isAuthenticated();

  useEffect(() => {
    if (!authenticated) {
      navigate("/login", { replace: true });
    }
  }, [authenticated, navigate]);

  if (!authenticated) return null;
  return <>{children}</>;
}

function RedirectIfAuth({ to }: { to: string }) {
  return isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Navigate to={to} replace />;
}

function RequireRole({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const auth = getAuth();
  if (!auth) return <Navigate to="/login" replace />;
  if (auth.user.isSuperAdmin) return <>{children}</>;
  if (!roles.includes(auth.user.role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  const navigate = useNavigate();
  const { sessionExpired, acknowledge } = useUnauthorizedWatcher();

  function handleSessionExpiredConfirm() {
    acknowledge();
    Sentry.setUser(null);
    clearAuth();
    navigate("/login", { replace: true });
  }

  return (
    <>
      <Toaster richColors position="top-right" />
      <SessionExpiredDialog open={sessionExpired} onConfirm={handleSessionExpiredConfirm} />
      <Routes>
      <Route path="/login" element={isAuthenticated() ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/politica-de-privacidad" element={<PrivacyPolicyPage />} />
      <Route path="/c/:clientId/s/:shipmentId" element={<PublicTripPlanPage />} />

      {/* Pages with a shared sidebar */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route
          path="/users"
          element={
            <RequireRole roles={R_USERS}>
              <UsersPage />
            </RequireRole>
          }
        />
        {/* Geography — admin + operator */}
        <Route
          path="/geo/countries"
          element={<RequireRole roles={R_GEO}><CountriesPage /></RequireRole>}
        />
        <Route
          path="/geo/states"
          element={<RequireRole roles={R_GEO}><StatesPage /></RequireRole>}
        />
        <Route
          path="/geo/cities"
          element={<RequireRole roles={R_GEO}><CitiesPage /></RequireRole>}
        />
        <Route
          path="/geo/cities/:id"
          element={<RequireRole roles={R_GEO}><CityDetailPage /></RequireRole>}
        />
        <Route
          path="/geo/routes"
          element={<RequireRole roles={R_GEO}><RoutesPage /></RequireRole>}
        />
        <Route
          path="/geo/routes/:id"
          element={<RequireRole roles={R_GEO}><RouteDetailPage /></RequireRole>}
        />
        {/* Viajes */}
        <Route
          path="/trips"
          element={<RequireRole roles={R_TRIPS}><TripsPage /></RequireRole>}
        />
        <Route
          path="/trips/service-types"
          element={<RequireRole roles={R_TRIPS}><ServiceTypesPage /></RequireRole>}
          />
        <Route
          path="/shipments"
          element={<RequireRole roles={R_SHIPMENT}><ShipmentsPage /></RequireRole>}
        />
        <Route
          path="/shipments/billing"
          element={<RequireRole roles={R_SHIPMENT}><ShipmentsBillingPage /></RequireRole>}
        />
        <Route
          path="/shipments/:id"
          element={<RequireRole roles={R_SHIPMENT}><ShipmentDetailPage /></RequireRole>}
        />
        <Route
          path="/parties"
          element={<RequireRole roles={R_PARTIES}><PartiesPage /></RequireRole>}
        />
        <Route
          path="/cargos/nacional"
          element={<RequireRole roles={R_CARGO}><CargasNacionalPage /></RequireRole>}
        />
        <Route
          path="/cargos/internacional"
          element={<RequireRole roles={R_CARGO}><CargasInternacionalPage /></RequireRole>}
        />
        <Route
          path="/cargos/batea"
          element={<RequireRole roles={R_CARGO}><CargasBateaPage /></RequireRole>}
        />
        <Route
          path="/cargos/products"
          element={<RequireRole roles={R_CARGO}><CargoProductsPage /></RequireRole>}
        />
        <Route
          path="/cargos/products/:id"
          element={<RequireRole roles={R_CARGO}><CargoProductDetailPage /></RequireRole>}
        />
        <Route
          path="/cargos/types"
          element={<RequireRole roles={R_CARGO}><CargoTypesPage /></RequireRole>}
        />
        <Route
          path="/cargos/informes"
          element={<RequireRole roles={R_CARGO}><CargasInformesPage /></RequireRole>}
        />
        <Route
          path="/batea/final-destinations"
          element={<RequireRole roles={R_BATEA}><FinalDestinationsPage /></RequireRole>}
        />
        <Route
          path="/unit-types"
          element={<RequireRole roles={R_UNITS}><UnitTypesPage /></RequireRole>}
        />
        <Route
          path="/units"
          element={<RequireRole roles={R_UNITS}><UnitsPage /></RequireRole>}
        />
        <Route
          path="/units/:id"
          element={<RequireRole roles={R_UNITS}><UnitDetailPage /></RequireRole>}
        />
        <Route
          path="/drivers"
          element={<RequireRole roles={R_DRIVERS}><DriversPage /></RequireRole>}
        />
        <Route
          path="/drivers/remitos"
          element={<RequireRole roles={R_DRIVERS}><DriverRemitosPage /></RequireRole>}
        />
        <Route
          path="/drivers/:id"
          element={<RequireRole roles={R_DRIVERS}><DriverDetailPage /></RequireRole>}
        />
        <Route
          path="/clients"
          element={<RequireRole roles={R_CLIENTS}><ClientsPage /></RequireRole>}
        />
        <Route
          path="/clients/:id"
          element={<RequireRole roles={R_CLIENTS}><ClientDetailPage /></RequireRole>}
        />
        <Route
          path="/relations/contracts"
          element={<RequireRole roles={R_CONTRACTS}><ContractsPage /></RequireRole>}
        />
        <Route
          path="/relations/contracts/:id"
          element={<RequireRole roles={R_CONTRACTS}><ContractDetailPage /></RequireRole>}
        />
        <Route
          path="/doc-types"
          element={<RequireRole roles={R_CONFIG}><DocTypesPage /></RequireRole>}
        />
        <Route
          path="/shipment-doc-types"
          element={<RequireRole roles={R_SHIPMENT_DOC_TYPES}><ShipmentDocTypesPage /></RequireRole>}
        />
        <Route
          path="/currencies"
          element={<RequireRole roles={R_CONFIG}><CurrenciesPage /></RequireRole>}
        />
        <Route
          path="/system/settings"
          element={<RequireRole roles={R_CONFIG}><AppSettingsPage /></RequireRole>}
        />
        <Route
          path="/pay-concepts"
          element={<RequireRole roles={R_CONFIG}><PayConceptsPage /></RequireRole>}
        />
        <Route
          path="/smart-gt"
          element={<RequireRole roles={R_SMARTGT}><SmartGTPage /></RequireRole>}
        />
        <Route
          path="/smart-gt/chofer"
          element={<RequireRole roles={R_SMARTGT}><DriverCrtsPage /></RequireRole>}
        />
        <Route
          path="/docs/smartgt-v2-formato"
          element={<RequireRole roles={R_DOCS}><SmartGtV2FormatPage /></RequireRole>}
        />
        <Route
          path="/smart-gt/import-logs"
          element={<RequireRole roles={R_AUDIT}><CrtImportLogsPage /></RequireRole>}
        />
        <Route
          path="/mobiles"
          element={<RequireRole roles={R_MOBILES}><MobilesPage /></RequireRole>}
        />
        <Route
          path="/system/kpi"
          element={<RequireRole roles={["admin"]}><KpiPage /></RequireRole>}
        />
        <Route
          path="/system/audit"
          element={<RequireRole roles={R_AUDIT}><AuditPage /></RequireRole>}
        />
        <Route
          path="/system/roles"
          element={<RequireRole roles={R_ROLES}><RolesPage /></RequireRole>}
        />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/reports/geo-import" element={<Navigate to={REPORT_GEO_IMPORT_PATH} replace />} />
        <Route
          path={REPORT_GEO_IMPORT_PATH}
          element={<RequireRole roles={R_REPORTS}><GeoImportReportPage /></RequireRole>}
        />
        <Route
          path="/reports/routes-import"
          element={<RequireRole roles={R_REPORTS}><RoutesImportReportPage /></RequireRole>}
        />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route
          path="/docs/admin"
          element={
            <RequireRole roles={R_USERS}>
              <AdminGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/operator"
          element={
            <RequireRole roles={R_DOCS}>
              <OperatorGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/crud-entidades"
          element={
            <RequireRole roles={R_USERS}>
              <CrudEntitiesGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/agregar-campos"
          element={
            <RequireRole roles={R_USERS}>
              <AddFieldsGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/db-drizzle"
          element={
            <RequireRole roles={R_USERS}>
              <DbDrizzleGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/mapeos-exportacion"
          element={
            <RequireRole roles={R_DOCS}>
              <ExportMappingsPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/calculo-alertas"
          element={
            <RequireRole roles={R_DOCS}>
              <WarningsCalculationPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/reglas-documentos-carga"
          element={
            <RequireRole roles={R_DOCS}>
              <CargoDocumentsRulesPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/seguridad-pendientes"
          element={
            <RequireRole roles={R_USERS}>
              <SecurityPendingPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/roles-permisos"
          element={
            <RequireRole roles={R_USERS}>
              <RolesPermissionsGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/batea-mobile"
          element={
            <RequireRole roles={R_DOCS}>
              <BateaMobileFlowPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/alertas-envios"
          element={
            <RequireRole roles={R_DOCS}>
              <ShipmentAlertsPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/estados-envios-viajes"
          element={
            <RequireRole roles={R_DOCS}>
              <ShipmentTripStatusesPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/cargas-movil-viajes"
          element={
            <RequireRole roles={R_DOCS}>
              <ShipmentMobileFlowPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/acciones-viajes"
          element={
            <RequireRole roles={R_DOCS}>
              <TripActionsPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/dividir-viaje"
          element={
            <RequireRole roles={R_DOCS}>
              <SplitTripPage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/exportacion-viajes"
          element={
            <RequireRole roles={R_DOCS}>
              <TripsExportGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/exportacion-cargas"
          element={
            <RequireRole roles={R_DOCS}>
              <CargoExportGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/importacion-facturacion"
          element={
            <RequireRole roles={R_DOCS}>
              <BillingImportGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/contratos"
          element={
            <RequireRole roles={R_DOCS}>
              <ContractsGuidePage />
            </RequireRole>
          }
        />
        <Route
          path="/docs/batea-multi-pierna"
          element={
            <RequireRole roles={R_DOCS}>
              <BateaMultiLegPage />
            </RequireRole>
          }
        />
      </Route>

      <Route path="/" element={<RedirectIfAuth to="/login" />} />
      <Route path="*" element={<RedirectIfAuth to="/login" />} />
    </Routes>
    </>
  );
}
